import { db } from '../config/firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  limit, 
  addDoc, 
  Timestamp,
  doc,
  getDoc,
  orderBy
} from 'firebase/firestore';
import { getClient } from './geminiService';
import { ragKnowledgeService } from './ragKnowledgeService';

/**
 * RAG Service
 * Handles Embedding generation and Vector Search logic for AI Agents
 * 
 * Now includes:
 * - Keyword storage and retrieval
 * - Optimization insights storage
 * - Campaign success patterns
 */

// ============================================================================
// TYPES
// ============================================================================

export interface OptimizationInsight {
  type: 'budget' | 'bidding' | 'keywords' | 'structure' | 'targeting';
  industry: string;
  action: string;
  result: 'success' | 'failure' | 'pending';
  metrics: {
    before: number;
    after: number;
    improvement?: number;
  };
  metadata?: any;
}

export interface CampaignSuccessPattern {
  industry: string;
  campaignType: 'search' | 'pmax' | 'display';
  keyFactors: string[];
  avgCtr: number;
  avgConversionRate: number;
  avgRoas: number;
  keywordCount: number;
  topKeywords: string[];
  metadata?: any;
}

export const ragService = {
  /**
   * Generate embedding for a given text using text-embedding-004
   * Uses Unified Google GenAI SDK: client.models.embedContent()
   */
  async generateEmbedding(text: string): Promise<number[]> {
    try {
      const client = getClient();
      
      // Unified SDK: client.models.embedContent() instead of model.embedContent()
      const result = await client.models.embedContent({
        model: "text-embedding-004",
        contents: text
      });
      
      // Unified SDK returns embeddings array
      return result.embeddings?.[0]?.values || result.embedding?.values || [];
    } catch (error) {
      console.error('Error generating embedding:', error);
      throw error;
    }
  },

  /**
   * Save a keyword with its embedding to the knowledge base
   */
  async saveKeywordToKnowledgeBase(orgId: string, data: {
    keyword: string;
    industry: string;
    performanceScore: number;
    metadata?: any;
  }) {
    try {
      const embedding = await this.generateEmbedding(data.keyword);
      
      const kbRef = collection(db, 'organizations', orgId, 'knowledgeBase', 'keywords', 'items');
      await addDoc(kbRef, {
        keyword: data.keyword,
        industry: data.industry.toLowerCase(),
        performanceScore: data.performanceScore,
        embedding: embedding, // Store the vector
        createdAt: Timestamp.now(),
        metadata: data.metadata || {}
      });
      
      console.log(`✅ Keyword "${data.keyword}" saved to Knowledge Base`);
    } catch (error) {
      console.error('Error saving to Knowledge Base:', error);
    }
  },

  /**
   * Query relevant keywords based on industry and similarity
   * Note: This is a hybrid approach using industry filtering first
   * since Firestore vector search requires specific setup.
   */
  async queryRelevantKeywords(orgId: string, industry: string, limitCount: number = 10): Promise<string[]> {
    try {
      // 1. Static Retrieval from Industry Templates
      const staticKeywords = ragKnowledgeService.getStaticKeywords(industry);
      
      // 2. Dynamic Retrieval from Firestore
      // For now, we use a simple industry-based query as a fallback 
      // if vector search is not yet fully indexed in the console.
      const kbRef = collection(db, 'organizations', orgId, 'knowledgeBase', 'keywords', 'items');
      const q = query(
        kbRef, 
        where('industry', '==', industry.toLowerCase()),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      const dynamicKeywords: string[] = [];
      snapshot.forEach(doc => {
        dynamicKeywords.push(doc.data().keyword);
      });

      // Merge and deduplicate
      return Array.from(new Set([...staticKeywords, ...dynamicKeywords])).slice(0, limitCount * 2);
    } catch (error) {
      console.error('Error querying Knowledge Base:', error);
      return [];
    }
  },

  // ============================================================================
  // OPTIMIZATION INSIGHTS
  // ============================================================================

  /**
   * Save an optimization insight to the knowledge base
   * Called after successful optimizations to enable learning
   */
  async saveOptimizationInsight(orgId: string, data: OptimizationInsight): Promise<void> {
    try {
      const kbRef = collection(db, 'organizations', orgId, 'knowledgeBase', 'optimizations', 'items');
      
      const improvement = data.metrics.before > 0 
        ? ((data.metrics.after - data.metrics.before) / data.metrics.before * 100)
        : 0;
      
      await addDoc(kbRef, {
        type: data.type,
        industry: data.industry.toLowerCase(),
        action: data.action,
        result: data.result,
        metrics: {
          ...data.metrics,
          improvement: improvement.toFixed(2)
        },
        createdAt: Timestamp.now(),
        metadata: data.metadata || {}
      });
      
      console.log(`✅ Optimization insight saved: ${data.type} (${data.result})`);
    } catch (error) {
      console.error('Error saving optimization insight:', error);
    }
  },

  /**
   * Query successful optimizations for an industry
   * Returns insights that can be used for future recommendations
   */
  async queryOptimizationInsights(
    orgId: string, 
    industry: string, 
    type?: OptimizationInsight['type'],
    limitCount: number = 10
  ): Promise<any[]> {
    try {
      const kbRef = collection(db, 'organizations', orgId, 'knowledgeBase', 'optimizations', 'items');
      
      let q = query(
        kbRef, 
        where('industry', '==', industry.toLowerCase()),
        where('result', '==', 'success'),
        limit(limitCount)
      );
      
      if (type) {
        q = query(
          kbRef,
          where('industry', '==', industry.toLowerCase()),
          where('type', '==', type),
          where('result', '==', 'success'),
          limit(limitCount)
        );
      }
      
      const snapshot = await getDocs(q);
      const insights: any[] = [];
      snapshot.forEach(doc => {
        insights.push({ id: doc.id, ...doc.data() });
      });
      
      return insights;
    } catch (error) {
      console.error('Error querying optimization insights:', error);
      return [];
    }
  },

  // ============================================================================
  // CAMPAIGN SUCCESS PATTERNS
  // ============================================================================

  /**
   * Save a successful campaign pattern for future learning
   */
  async saveCampaignSuccessPattern(orgId: string, data: CampaignSuccessPattern): Promise<void> {
    try {
      const kbRef = collection(db, 'organizations', orgId, 'knowledgeBase', 'campaignPatterns', 'items');
      
      await addDoc(kbRef, {
        industry: data.industry.toLowerCase(),
        campaignType: data.campaignType,
        keyFactors: data.keyFactors,
        avgCtr: data.avgCtr,
        avgConversionRate: data.avgConversionRate,
        avgRoas: data.avgRoas,
        keywordCount: data.keywordCount,
        topKeywords: data.topKeywords,
        createdAt: Timestamp.now(),
        metadata: data.metadata || {}
      });
      
      console.log(`✅ Campaign success pattern saved for ${data.industry}`);
    } catch (error) {
      console.error('Error saving campaign success pattern:', error);
    }
  },

  /**
   * Query campaign success patterns for an industry
   */
  async queryCampaignPatterns(
    orgId: string, 
    industry: string,
    campaignType?: 'search' | 'pmax' | 'display',
    limitCount: number = 5
  ): Promise<CampaignSuccessPattern[]> {
    try {
      const kbRef = collection(db, 'organizations', orgId, 'knowledgeBase', 'campaignPatterns', 'items');
      
      let q = query(
        kbRef, 
        where('industry', '==', industry.toLowerCase()),
        orderBy('avgRoas', 'desc'),
        limit(limitCount)
      );
      
      if (campaignType) {
        q = query(
          kbRef,
          where('industry', '==', industry.toLowerCase()),
          where('campaignType', '==', campaignType),
          orderBy('avgRoas', 'desc'),
          limit(limitCount)
        );
      }
      
      const snapshot = await getDocs(q);
      const patterns: CampaignSuccessPattern[] = [];
      snapshot.forEach(doc => {
        patterns.push(doc.data() as CampaignSuccessPattern);
      });
      
      return patterns;
    } catch (error) {
      console.error('Error querying campaign patterns:', error);
      return [];
    }
  },

  // ============================================================================
  // AGGREGATE LEARNING
  // ============================================================================

  /**
   * Get learning summary for an industry
   * Aggregates insights from keywords, optimizations, and patterns
   */
  async getIndustryLearningSummary(orgId: string, industry: string): Promise<{
    topKeywords: string[];
    successfulOptimizations: any[];
    bestCampaignPatterns: CampaignSuccessPattern[];
    recommendations: string[];
  }> {
    try {
      const [keywords, optimizations, patterns] = await Promise.all([
        this.queryRelevantKeywords(orgId, industry, 20),
        this.queryOptimizationInsights(orgId, industry, undefined, 10),
        this.queryCampaignPatterns(orgId, industry, undefined, 5)
      ]);

      // Generate recommendations based on learned data
      const recommendations: string[] = [];
      
      // Budget recommendations from optimizations
      const budgetOptimizations = optimizations.filter(o => o.type === 'budget');
      if (budgetOptimizations.length > 0) {
        const avgImprovement = budgetOptimizations.reduce((sum, o) => sum + parseFloat(o.metrics.improvement || 0), 0) / budgetOptimizations.length;
        if (avgImprovement > 0) {
          recommendations.push(`Budget increases have historically improved performance by ${avgImprovement.toFixed(1)}% in this industry`);
        }
      }

      // Keyword recommendations
      if (keywords.length > 5) {
        recommendations.push(`Consider using proven keywords: ${keywords.slice(0, 5).join(', ')}`);
      }

      // Pattern-based recommendations
      if (patterns.length > 0) {
        const bestPattern = patterns[0];
        if (bestPattern.avgRoas > 3) {
          recommendations.push(`Top performing campaigns in this industry achieve ${(bestPattern.avgRoas * 100).toFixed(0)}% ROAS with ${bestPattern.keywordCount} keywords`);
        }
      }

      return {
        topKeywords: keywords.slice(0, 10),
        successfulOptimizations: optimizations,
        bestCampaignPatterns: patterns,
        recommendations
      };
    } catch (error) {
      console.error('Error getting industry learning summary:', error);
      return {
        topKeywords: [],
        successfulOptimizations: [],
        bestCampaignPatterns: [],
        recommendations: []
      };
    }
  }
};

